import { useEffect, useState } from "react"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { IOrderListProjection } from "../../../types/order/IOrderListProjection"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { MenuClient } from "../MenuClient"
import { Header } from "../../common/header/Header"
import { IMG } from "../../common/CONST"
import { getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { OrderStatus } from "../../../types/order/OrderStatus"
import { IOrderStatus } from "../../../types/order/IOrderStatus"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { IClientBalanceDto } from "../../../types/client/IClientBalanceDto"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import { useNavigate } from "react-router-dom"
import { ORDER_NEW_ROUTE } from "@/routes"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import Input from "@/ui/components/Input"
import AXIOS from "@/requests/_axios"

export const ClientOrderList = () => {
  const navigate = useNavigate()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [orders, setOrders] = useState<IOrderListProjection[]>([])
  const [filteredOrders, setFilteredOrders] = useState<IOrderListProjection[]>([])
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const [balance, setBalance] = useState<IClientBalanceDto>()
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "ORDER"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  const fetchOrders = () => {
    setSearch("")

    TrinityAPI.getOrderList(
      status,
      response => {
        if (response.status === 200) {
          setOrders(response.data)
          setFilteredOrders(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения ордеров: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      }
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchOrders()
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await AXIOS.get("/trinity/api/v1/unload/xls/orders")

      if (response.status === 200) {
        const { data } = response

        const element = document.createElement("a")
        element.href = `data:application/vnd.ms-excel;base64,${response.data.fileBase64}`
        element.download = data.fileName
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
      }
    } catch (err) {
      setIsDownloading(false)
    }
    setIsDownloading(false)
  }

  useEffect(() => {
    fetchOrders()
  }, [status])

  useEffect(() => {
    if (!search) {
      setFilteredOrders(orders)
      return
    }

    setFilteredOrders(
      orders.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.country && r.country.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.amount && round2Digits(Number(r.amount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    TrinityAPI.getBalance(response => {
      if (response.status === 200) {
        setBalance(response.data)
      }
    })

    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      <MenuClient active={"order"} />
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Ордера</h1>

          <section className="profile-balance">
            <div className="profile-balance-card">
              <div className="balance-info">
                <p className="name-balance">Баланс</p>
                <p className="balance-num">
                  {balance?.amount ? (
                    <>
                      {spacedNum(balance.amount / 100)}
                      <span className="valuta">USD</span>
                    </>
                  ) : (
                    <>
                      0 <span className="valuta">USD</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="good-rate">
              <div className="balance-info">
                <p className="name-balance">Месячный оборот</p>
                <p className="balance-num-negative">
                  {balance?.monthlyVolume ? spacedNum(balance.monthlyVolume / 100) : "0"} USD
                </p>
              </div>
            </div>
          </section>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.deals} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" onClick={refetchData} loading={isRefetchLoading}>
                Обновить список ордеров
              </Button>
            </div>
            <div className="flex-row-10 flex-wrap-mobile">
              <Button onClick={handleDownload} variant="black" loading={isDownloading}>
                Сверка ордеров
              </Button>
              <Button
                onClick={() => navigate(ORDER_NEW_ROUTE)}
                leftIcon={<img src={IMG.plus_btn} alt="" className="img-btn" />}
              >
                Добавить
              </Button>
            </div>
          </section>

          <section className="search-block">
            <Input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item cols-deals">Название и страна</li>
                <li className="header-global-item width-200">Банк</li>
                <li className="header-global-item width-200">Сумма</li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item width-200">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option key="EMPTY" value="">
                      Статус
                    </option>
                    {Object.keys(OrderStatus).map((key, index) =>
                      OrderStatus[key as keyof IOrderStatus][1] ? (
                        <option value={key} key={index.toString()}>
                          {OrderStatus[key as keyof IOrderStatus][1]}
                        </option>
                      ) : null
                    )}
                  </select>
                </li>
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredOrders.length > 0 ? (
                    filteredOrders.map(o => (
                      <li
                        className={`global-item${
                          o.status === "PAID" ||
                          o.status === "SWIFT_DONE" ||
                          o.status === "INQ_SWIFT_DONE" ||
                          o.status === "INQUIRY_DONE"
                            ? "-active"
                            : ""
                        }`}
                        key={o.id}
                      >
                        <div className="global-scroll-block" onClick={() => window.open("/order/" + o.id, "_blank")}>
                          <p className="global-item-text width-130">
                            <span>№{o.id}</span>
                            <span dangerouslySetInnerHTML={{ __html: getMskTime(o.createdAt || "") || "" }} />
                          </p>
                          <p className="deal-item-flex cols-deals">
                            <span className="deal-country">{o.country?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{o.contractor || "-"}</span>
                          </p>
                          <p className="global-item-text width-200">{o.bankName?.toUpperCase() || "-"}</p>
                          <p className="global-item-text width-200">
                            {o.amount ? spacedNum(round2Digits(Number(o.amount) / 100)) : "-"}
                          </p>
                          <p className="global-item-text globak-num width-100">{o.currency || "-"}</p>
                          <p className={`status width-200`}>
                            {o.status ? OrderStatus[o.status as keyof IOrderStatus][1] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(o.id?.toString(), o.userId)}>
                          {getUnreadCounter(o.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(o.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Ордеров не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
